/**
 * Custom hook for OCR image processing functionality
 */

import React from 'react';
import { processImageFile, OCRServiceError } from '../../../../services/ocr.service';
import type { OCRFieldType, OCRLoadingState } from '../types';

export const useOCR = (handleTruckUpdate: (truckId: string, field: string, value: string) => Promise<void>) => {
  const [ocrLoading, setOcrLoading] = React.useState<OCRLoadingState>({});
  const vinInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});
  const licenseInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});

  /**
   * Handle camera capture for VIN
   */
  const handleVinCamera = (truckId: string) => {
    const input = vinInputRefs.current[truckId];
    if (input) input.click();
  };

  /**
   * Handle camera capture for License Plate
   */
  const handleLicenseCamera = (truckId: string) => {
    const input = licenseInputRefs.current[truckId];
    if (input) input.click();
  };

  /**
   * Process captured image with OCR
   */
  const handleImageCapture = async (
    truckId: string,
    fieldType: OCRFieldType,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('🔵 handleImageCapture called', { truckId, fieldType, fileName: file.name });

    // Set loading state
    setOcrLoading(prev => ({ ...prev, [truckId]: fieldType === 'vin' ? 'vin' : 'license' }));

    try {
      // Process image with OCR service
      console.log('🔵 Calling processImageFile...');
      const result = await processImageFile(file, fieldType);
      console.log('🔵 OCR result received:', result);

      if (result.success && result.extractedText) {
        console.log('✅ OCR succeeded:', {
          extractedText: result.extractedText,
          vehicleName: result.vehicleName,
          fieldType: result.fieldType
        });

        // Update truck field with extracted text
        console.log(`🔵 Updating ${fieldType} field with:`, result.extractedText);
        await handleTruckUpdate(truckId, fieldType, result.extractedText);

        // If VIN was extracted and vehicle name was decoded, update vehicle name
        if (fieldType === 'vin' && result.vehicleName) {
          console.log('✅ VIN vehicle name received, updating name field:', result.vehicleName);
          await handleTruckUpdate(truckId, 'name', result.vehicleName);
        } else if (fieldType === 'vin') {
          console.log('⚠️ VIN extracted but no vehicle name returned');
        }
      } else {
        console.error('❌ OCR failed:', result.error);
        alert(result.error || 'Failed to extract text from image');
      }
    } catch (error) {
      console.error('❌ Image processing error:', error);
      if (error instanceof OCRServiceError) {
        alert(error.message);
      } else {
        alert('Failed to process image. Please try again.');
      }
    } finally {
      // Clear loading state
      setOcrLoading(prev => ({ ...prev, [truckId]: null }));
      // Clear file input
      event.target.value = '';
    }
  };

  return {
    ocrLoading,
    vinInputRefs,
    licenseInputRefs,
    handleVinCamera,
    handleLicenseCamera,
    handleImageCapture,
  };
};
